"use client"

// src/App.jsx
import React, { Suspense, lazy, useEffect, useState, useRef, useMemo, useCallback } from "react"
import { OutageProvider } from "./context/OutageContext"
import LoadingSpinner from "./components/LoadingSpinner"
import GlobalOutageOverlay from "./components/GlobalOutageOverlay"
import CourseManageRoute from "./components/CourseManageRoute"
import AdminUsuarioDetalle from "./pages/admin/AdminUsuarioDetalle"
import AuthFotoOnboarding from "./pages/AuthFotoOnboarding"
import AdminAnyRoute from "./components/AdminAnyRoute"
import AdminGlobalRoute from "./components/AdminGlobalRoute"
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Outlet,
  useParams,
  useLocation,
  matchPath,
  useOutletContext,
  Navigate,
  useNavigate
} from "react-router-dom"

import ScheduleManager from "./components/examenes/ScheduleManager"
import { AuthProvider, useAuth } from "./context/AuthContext"
import { FuncionProvider, useFuncion } from "./context/FuncionContext"
import { CourseStructureProvider } from "./context/CourseStructureContext"

import ProtectedRoute from "./components/ProtectedRoute"
import AdminRoute from "./components/AdminRoute"
import CourseEnrolledRoute from "./components/CourseEnrolledRoute"

import SessionExpiryModal from "./components/SessionExpiryModal"
import ForcePasswordChangeModal from "./components/ForcePasswordChangeModal"
import Header from "./components/Header"
import Footer from "./components/Footer"

// Páginas (no-admin)
import Home from "./pages/Home"
import Login from "./pages/Login"
import VerRecurso from "./pages/verRecurso"
import CursoDetalle from "./pages/CursoDetalle"
import ComponenteTareas from "./pages/ComponenteTareas"
import Examen from "./pages/Examen"
import ResolverTarea from "./pages/tareas/ResolverTarea"
import AgendarExamen from "./pages/AgendarExamen"
import ModuloRecursos from "./pages/ModuloRecursos"
import Ayuda from "./pages/Ayuda"
import ProfileView from "./pages/profile/ProfileView"
import NivelTest from "./pages/NivelTest"
import NivelTestReview from "./pages/NivelTestReview"
import Store from "./pages/Store"

// Admin (carga diferida para optimizar)
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"))
const AdminUsuarios = lazy(() => import("./pages/admin/AdminUsuarios"))
const AdminCursos = lazy(() => import("./pages/admin/AdminCursos"))
const GestionarCurso = lazy(() => import("./pages/admin/GestionarCurso"))
const AdminReportes = lazy(() => import("./pages/admin/AdminReportes"))
const AdminConfiguracion = lazy(() => import("./pages/admin/AdminConfiguracion"))
const AdminExamenes = lazy(() => import("./pages/admin/AdminExamenes"))
const AdminTienda = lazy(() => import("./pages/admin/AdminTienda"))
const AdminCursoDetalle = lazy(() => import("./pages/admin/AdminCursoDetalle"))
const AdminFacultadDetalle = lazy(() => import("./pages/admin/AdminFacultadDetalle"))
const AdminFacultades = lazy(() => import("./pages/admin/AdminFacultades"))
const AdminProctoring = lazy(() => import("./pages/admin/AdminProctoring"))
const AdminTestNivel = lazy(() => import("./pages/admin/AdminTestNivel"))

const toBool = (v) => {
  const s = String(v).toLowerCase()
  return v === true || v === 1 || s === "1" || s === "true" || s === "t"
}

const normalizeRequirementLevel = (v) => {
  const s = String(v || "no_requerido").toLowerCase().trim()
  if (["requerido", "opcional", "no_requerido"].includes(s)) return s
  return "no_requerido"
}

const deriveExamPolicyFromBooking = (b) => {
  const isVirtual = toBool(
    b?.is_virtual ??
    b?.es_virtual ??
    b?.horario_is_virtual ??
    b?.he_is_virtual ??
    b?.horario_virtual ??
    b?.horario?.is_virtual ??
    false
  )

  const camaraNivel = normalizeRequirementLevel(
    b?.camara_nivel ??
    b?.horario_camara_nivel ??
    b?.he_camara_nivel ??
    b?.horario?.camara_nivel ??
    "no_requerido"
  )

  const microfonoNivel = normalizeRequirementLevel(
    b?.microfono_nivel ??
    b?.horario_microfono_nivel ??
    b?.he_microfono_nivel ??
    b?.horario?.microfono_nivel ??
    "no_requerido"
  )

  const presencialProctor = toBool(
    b?.proctor_presencial ??
    b?.presencial_proctoring ??
    b?.requiere_camara_microfono ??
    b?.horario_requiere_camara_microfono ??
    b?.he_requiere_camara_microfono ??
    b?.horario?.requiere_camara_microfono ??
    false
  )

  const requireCamMic =
    isVirtual ||
    presencialProctor ||
    camaraNivel === "requerido" ||
    microfonoNivel === "requerido" ||
    camaraNivel === "opcional" ||
    microfonoNivel === "opcional"

  return {
    isVirtual,
    requireCamMic,
    camaraNivel,
    microfonoNivel,
  }
}


/**
 * ExamGate:
 * - Resuelve el curso por código
 * - Si hay agendamiento próximo => renderiza <Examen />
 * - Si no, muestra <AgendarExamen />
 * (Se coloca DENTRO de CourseEnrolledRoute para exigir inscripción a estudiantes)
 */
// ✅ Reemplaza ExamGate completo por este (src/App.jsx)
function ExamGate() {
  const { codigoCurso } = useParams();
  const { resolveStudentCourseByCode, fetchMyExamBookings } = useFuncion();
  const location = useLocation()
  const navigate = useNavigate()
  const { pathname, search, state } = location

  const fnsRef = useRef({ resolveStudentCourseByCode, fetchMyExamBookings });

  const startIntentKey = useMemo(() => {
    return `exam:start-intent:${codigoCurso}`
  }, [codigoCurso])

  const readStartIntent = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(startIntentKey)
      if (!raw) return null

      const parsed = JSON.parse(raw)
      const ts = Number(parsed?.ts || 0)

      // La intención solo vale 2 minutos.
      if (!Number.isFinite(ts) || Date.now() - ts > 2 * 60 * 1000) {
        sessionStorage.removeItem(startIntentKey)
        return null
      }

      return parsed
    } catch {
      return null
    }
  }, [startIntentKey])

  useEffect(() => {
    fnsRef.current = { resolveStudentCourseByCode, fetchMyExamBookings };
  }, [resolveStudentCourseByCode, fetchMyExamBookings]);

  const qs = useMemo(() => new URLSearchParams(search), [search])

  const routeStartRequested =
    state?.examStart === true ||
    readStartIntent()?.examStart === true

  // ✅ Compatibilidad temporal: si todavía existe ?start=1, lo aceptamos,
  // pero luego limpiamos la URL.
  const legacyStartRequested = qs.get("start") === "1"

  const startRequested = routeStartRequested || legacyStartRequested

  const [loading, setLoading] = useState(true);
  const [canStartExam, setCanStartExam] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [isWindowOpen, setIsWindowOpen] = useState(false);
  const [isVirtualExam, setIsVirtualExam] = useState(false);
  const [requireCamMic, setRequireCamMic] = useState(false)
  const [err, setErr] = useState("");

  const ymd = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const da = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
  };
  const parseHHMMSS = (t) => {
    if (!t) return null;
    const [hh, mm, ss = "0"] = String(t).split(":");
    const h = Number.parseInt(hh, 10);
    const m = Number.parseInt(mm, 10);
    const s = Number.parseInt(ss, 10);
    if (Number.isNaN(h) || Number.isNaN(m) || Number.isNaN(s)) return null;
    return h * 3600 + m * 60 + s;
  };
  const nowSeconds = () => {
    const n = new Date();
    return n.getHours() * 3600 + n.getMinutes() * 60 + n.getSeconds();
  };

  const isPresentationCompleted = (b) => {
    const presObj =
      b?.presentacion ||
      b?.presentation ||
      (Array.isArray(b?.presentaciones) && b.presentaciones[0]) ||
      (Array.isArray(b?.attempts) && b.attempts[0]) ||
      null;

    const estadoRaw =
      b?.presentacion_estado ??
      b?.estado_presentacion ??
      presObj?.estado ??
      presObj?.status ??
      "";

    const estado = String(estadoRaw || "").toLowerCase().trim();

    // ✅ estados explícitos
    if (
      estado === "completado" ||
      estado === "completada" ||
      estado === "finalizado" ||
      estado === "finalizada"
    ) return true;

    // ✅ evidencia fuerte de intento/presentación + fecha fin
    const presId =
      b?.presentacion_id ??
      b?.presentation_id ??
      presObj?.id ??
      presObj?.presentacion_id ??
      presObj?.attempt_id ??
      null;

    const fechaFin =
      b?.presentacion_fecha_finalizacion ??
      presObj?.fecha_finalizacion ??
      presObj?.finished_at ??
      null;

    return Boolean(presId && fechaFin);
  };

  useEffect(() => {
    if (!legacyStartRequested) return

    const cleanPath = `/curso/${codigoCurso}/examen`

    try {
      sessionStorage.setItem(
        startIntentKey,
        JSON.stringify({
          examStart: true,
          ts: Date.now(),
        })
      )
    } catch { }

    navigate(cleanPath, {
      replace: true,
      state: {
        examStart: true,
      },
    })
  }, [
    legacyStartRequested,
    codigoCurso,
    navigate,
    startIntentKey,
  ])

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setErr("");
        setCanStartExam(false);
        setHasCompleted(false);
        setIsWindowOpen(false);
        setIsVirtualExam(false);
        setRequireCamMic(false)

        const course = await fnsRef.current.resolveStudentCourseByCode?.(codigoCurso, { signal: ctrl.signal });
        const courseId = Number(course?.id);
        if (!courseId) {
          if (alive) setLoading(false);
          return;
        }

        const resUpcoming = await fnsRef.current.fetchMyExamBookings?.({
          curso_id: courseId,
          upcoming: true,
          signal: ctrl.signal,
        });

        let bookings = Array.isArray(resUpcoming?.bookings) ? resUpcoming.bookings : resUpcoming?.items || [];

        // 1) completados en upcoming
        let completedInUpcoming = bookings.some(isPresentationCompleted);

        // 2) si no hay completados, traemos todo para cubrir finalizados
        if (!completedInUpcoming) {
          const resAll = await fnsRef.current.fetchMyExamBookings?.({
            curso_id: courseId,
            upcoming: false,
            signal: ctrl.signal,
          });
          const allBookings = Array.isArray(resAll?.bookings) ? resAll.bookings : resAll?.items || [];
          const completedInAll = allBookings.some(isPresentationCompleted);
          if (completedInAll) completedInUpcoming = true;
          if (!bookings.length) bookings = allBookings;
        }

        const today = ymd(new Date());
        const todayBookings = bookings.filter((b) => ymd(b?.fecha_examen) === today);

        const nowS = nowSeconds();
        const windowOpen = todayBookings.some((b) => {
          const start = parseHHMMSS(b?.hora_inicio);
          const end = parseHHMMSS(b?.hora_fin);
          if (start == null || end == null) return false;
          return nowS >= start && nowS <= end;
        });

        const bookingForPolicy = (windowOpen ? todayBookings[0] : bookings[0]) || null
        const policy = bookingForPolicy ? deriveExamPolicyFromBooking(bookingForPolicy) : { isVirtual: false, requireCamMic: false }

        if (alive) {
          setHasCompleted(Boolean(completedInUpcoming))
          setIsWindowOpen(Boolean(windowOpen))

          // Mantengo tu estado para UI si quieres mostrar “virtual/presencial”
          setIsVirtualExam(Boolean(policy.isVirtual))

          // ✅ NUEVO: guarda si requiere permisos (cam/mic)
          // (necesitas crear este state abajo, mira punto C)
          setRequireCamMic(Boolean(policy.requireCamMic))

          setCanStartExam(Boolean(windowOpen))
          setLoading(false)
        }

      } catch (e) {
        if (alive && e?.name !== "AbortError") {
          setErr(e?.message || "No se pudo determinar tu agendamiento.");
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [codigoCurso]);

  if (err) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "#ef4444" }}>
        <h3>Error:</h3>
        <p>{err}</p>
        <button onClick={() => window.location.reload()}>Recargar página</button>
      </div>
    );
  }

  if (loading) return <LoadingSpinner message="Verificando tu agendamiento…" />;

  // ✅ IMPORTANTE: Si NO pidió start=1, SIEMPRE mostramos AgendarExamen
  if (!startRequested) {
    return (
      <AgendarExamen
        gateState={{
          canStartExam,
          isWindowOpen,
          isVirtualExam,
          hasCompleted,
        }}
      />
    );
  }

  // Si pidió iniciar pero no puede, lo dejamos en agendamiento con el estado
  if (!canStartExam) {
    return (
      <AgendarExamen
        gateState={{
          canStartExam,
          isWindowOpen,
          isVirtualExam,
          hasCompleted,
        }}
      />
    );
  }

  return <Examen />;
}


/**
 * Layout con Header/Footer y el modal de sesión
 */
function ChromeLayout() {
  const [footerVisible, setFooterVisible] = React.useState(true)
  const [headerVisible, setHeaderVisible] = React.useState(true)

  // Rutas donde NO debe existir footer
  const HIDE_FOOTER_PATTERNS = [
    "/login",
    "/identificacion"
  ]

  const HIDE_HEADER_FOOTER = [
    "/login",
    "/ayuda",
    "/curso/:codigoCurso/ayuda",
    "/curso/:codigoCurso/tarea/:tareaId/resolver",
    "/curso/:codigoCurso/tarea/:tareaId/resolver/*",
    "/curso/:codigoCurso/nivel/:nivelId/test/:presentacionId",
    "/curso/:codigoCurso/nivel/:nivelId/test/review",
    "/identificacion",
  ]
  const { pathname } = useLocation()

  // Verificamos si el path actual coincide con alguno
  const hideFooterByRoute = HIDE_FOOTER_PATTERNS.some((p) => matchPath({ path: p, end: false }, pathname))
  const hideFooterHeaderByRoute = HIDE_HEADER_FOOTER.some((p) => matchPath({ path: p, end: false }, pathname))

  const showFooter = footerVisible && !hideFooterByRoute
  const showHeader = headerVisible && !hideFooterHeaderByRoute



  return (
    <div className="app" style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {showHeader && (
        <div id="chrome-header">
          <Header />
        </div>
      )}

      <main style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Outlet context={{ footerVisible, setFooterVisible, headerVisible, setHeaderVisible }} />
      </main>

      {showFooter && (
        <div id="chrome-footer">
          <Footer />
        </div>
      )}

      <SessionExpiryModal />
      <ForcePasswordChangeModal />
    </div>
  )
}

/**
 * Agrupa rutas que requieren:
 * - Sesión activa (ProtectedRoute)
 * - Estar inscrito en el curso (CourseEnrolledRoute) excepto admin/docente (permitidos)
 */
function CourseLayout() {
  // ⬇️ tomamos el contexto del layout superior (ChromeLayout)
  const parentCtx = useOutletContext?.() || {}
  return (
    <ProtectedRoute>
      <CourseEnrolledRoute>
        {/* ⬇️ lo reenviamos al Outlet de este layout */}
        <Outlet context={parentCtx} />
      </CourseEnrolledRoute>
    </ProtectedRoute>
  )
}

/**
 * Agrupa rutas de administración con protección y rol admin
 */
function AdminLayout() {
  const parentCtx = useOutletContext?.() || {}
  return (
    <ProtectedRoute>
      <Outlet context={parentCtx} />
    </ProtectedRoute>
  )
}

function RootGate() {
  const { user, currentRole, loading } = useAuth();
  const { pathname } = useLocation();

  if (loading) return <LoadingSpinner message="Cargando sesión…" />;

  // Rol activo (preferido)
  const role = String(currentRole || "").toLowerCase().trim();

  const isGlobalAdminActive =
    role === "administrador" || role === "admin" || role === "superadmin";

  const isFacultyAdminActive =
    role === "admin_facultad" || role === "coordinador_facultad";

  // ✅ Fallback: si currentRole está vacío, inferimos por datos del user
  const hasFacultyAssignments =
    Array.isArray(user?.admin_facultades) && user.admin_facultades.length > 0;

  // OJO: si tu user trae roles en user.roles (claims), también puedes inferir admin global
  const tokenRoles = Array.isArray(user?.roles)
    ? user.roles.map(r => String(r?.nombre ?? r?.rol ?? r?.role ?? r).toLowerCase())
    : [];

  const isGlobalAdminByToken =
    tokenRoles.includes("administrador") || tokenRoles.includes("admin") || tokenRoles.includes("superadmin");

  // ✅ Si el rol activo no vino aún, aplicamos inferencia
  const shouldTreatAsFacultyAdmin = isFacultyAdminActive || (!role && hasFacultyAssignments);
  const shouldTreatAsGlobalAdmin = isGlobalAdminActive || (!role && isGlobalAdminByToken);

  if (pathname === "/" || pathname === "") {
    if (shouldTreatAsGlobalAdmin) return <Navigate to="/admin/facultades" replace />;
    if (shouldTreatAsFacultyAdmin) return <Navigate to="/admin" replace />; // o "/admin/cursos"
    return <Home />;
  }

  return <Home />;
}
const App = () => {

  return (
    <Router>
      <AuthProvider>
        <OutageProvider>
          <FuncionProvider>
            <CourseStructureProvider>
              <Suspense fallback={<div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>Cargando…</div>}>
                {/* 👇 Overlay global */}
                <GlobalOutageOverlay />
                <Routes>
                  {/* Layout principal con header/footer */}
                  <Route element={<ChromeLayout />}>
                    {/* Públicas */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/ayuda" element={<Ayuda />} />
                    <Route
                      path="/identificacion"
                      element={
                        <ProtectedRoute skipPhotoGate>
                          <AuthFotoOnboarding />
                        </ProtectedRoute>
                      }
                    />


                    {/* Inicio y vistas generales protegidas */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <RootGate />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/perfil"
                      element={
                        <ProtectedRoute>
                          <ProfileView />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/visualizar"
                      element={
                        <ProtectedRoute>
                          <VerRecurso />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/store"
                      element={
                        <ProtectedRoute>
                          <Store />
                        </ProtectedRoute>
                      }

                    />
                    {/* ===== Grupo de rutas del curso (aplica CourseEnrolledRoute) ===== */}
                    <Route path="/curso/:codigoCurso" element={<CourseLayout />}>
                      {/* Overview del curso */}
                      <Route index element={<CursoDetalle />} />

                      {/* Ayuda dentro del curso */}
                      <Route path="ayuda" element={<Ayuda />} />

                      {/* Examen (gatea por agendamiento) */}
                      <Route path="examen" element={<ExamGate />} />

                      {/* Agendar examen */}
                      <Route path="agendar-examen" element={<AgendarExamen />} />

                      {/* Tareas */}
                      <Route path="tarea/:structureId" element={<ComponenteTareas />} />
                      <Route path="tarea/:tareaId/resolver" element={<ResolverTarea />} />

                      {/* Recursos de módulo */}
                      <Route path="modulo/:moduloId/recursos" element={<ModuloRecursos />} />

                      {/* Test de nivel */}
                      <Route path="nivel/:nivelId/test/:presentacionId" element={<NivelTest />} />
                      {/* ✅ Nuevo: Revisión de intentos del test de nivel */}
                      <Route path="nivel/:nivelId/test/review" element={<NivelTestReview />} />

                      {/* 👇 NUEVO: Tienda dentro del curso */}
                      <Route path="store" element={<Store />} />
                    </Route>

                    {/* ===== Grupo de rutas Admin ===== */}
                    <Route path="/admin" element={<AdminLayout />}>
                      {/* ✅ Admin-only */}
                      <Route
                        index
                        element={
                          <AdminRoute>
                            <AdminDashboard />
                          </AdminRoute>
                        }
                      />

                      <Route
                        path="usuarios"
                        element={
                          <AdminRoute>
                            <AdminUsuarios />
                          </AdminRoute>
                        }
                      />

                      <Route
                        path="usuarios/:userId"
                        element={
                          <AdminRoute>
                            <AdminUsuarioDetalle />
                          </AdminRoute>
                        }
                      />

                      <Route
                        path="facultades"
                        element={
                          <AdminGlobalRoute>
                            <AdminFacultades />
                          </AdminGlobalRoute>
                        }
                      />

                      <Route path="facultades/:facultadId"
                        element={
                          <AdminGlobalRoute>
                            <AdminFacultadDetalle />
                          </AdminGlobalRoute>
                        }
                      />

                      <Route
                        path="facultades/:facultadId/cursos"
                        element={
                          <AdminAnyRoute>
                            <AdminCursos />
                          </AdminAnyRoute>
                        }
                      />

                      <Route
                        path="cursos"
                        element={
                          <AdminAnyRoute>
                            <AdminCursos />
                          </AdminAnyRoute>
                        }
                      />

                      {/* ✅ Staff por curso (Docente/Asistente con permisos): NO pasa por AdminRoute */}
                      <Route
                        path="cursos/:cursoId"
                        element={
                          <CourseManageRoute>
                            <AdminCursoDetalle />
                          </CourseManageRoute>
                        }
                      />

                      <Route
                        path="cursos/:cursoId/gestionar"
                        element={
                          <CourseManageRoute requiredPermission="editar_estructura_curso">
                            <Suspense fallback={<LoadingSpinner message="Cargando…" />}>
                              <GestionarCurso />
                            </Suspense>
                          </CourseManageRoute>
                        }
                      />

                      <Route
                        path="cursos/:cursoId/test-nivel/:nivelId"
                        element={
                          <CourseManageRoute requiredPermission="editar_estructura_curso">
                            <Suspense fallback={<LoadingSpinner message="Cargando..." />}>
                              <AdminTestNivel />
                            </Suspense>
                          </CourseManageRoute>
                        }
                      />

                      <Route
  path="cursos/:cursoId/banco-preguntas"
  element={
    <CourseManageRoute requiredPermission="editar_estructura_curso">
      <Suspense fallback={<LoadingSpinner message="Cargando…" />}>
        <GestionarCurso />
      </Suspense>
    </CourseManageRoute>
  }
/>

                      <Route
                        path="reportes"
                        element={
                          <AdminRoute>
                            <AdminReportes />
                          </AdminRoute>
                        }
                      />

                      <Route
                        path="configuracion"
                        element={
                          <AdminRoute>
                            <AdminConfiguracion />
                          </AdminRoute>
                        }
                      />

                      <Route
                        path="examenes"
                        element={
                          <AdminRoute>
                            <AdminExamenes />
                          </AdminRoute>
                        }
                      >
                        <Route index element={<Navigate to="horarios-examenes" replace />} />
                        <Route path="horarios-examenes" element={<ScheduleManager />} />
                        <Route path="proctoring" element={<AdminProctoring />} />
                        <Route path="proctoring/:horarioId" element={<AdminProctoring />} />
                      </Route>

                      <Route
                        path="cursos/:cursoId/examenes"
                        element={
                          <CourseManageRoute requiredPermission="gestionar_examenes">
                            <Suspense fallback={<LoadingSpinner message="Cargando…" />}>
                              <AdminExamenes />
                            </Suspense>
                          </CourseManageRoute>
                        }
                      >
                        <Route index element={<Navigate to="horarios-examenes" replace />} />
                        <Route path="horarios-examenes" element={<ScheduleManager />} />
                        <Route path="proctoring" element={<AdminProctoring />} />
                        <Route path="proctoring/:horarioId" element={<AdminProctoring />} />
                      </Route>

                      <Route
                        path="tienda"
                        element={
                          <AdminRoute>
                            <AdminTienda />
                          </AdminRoute>
                        }
                      />
                    </Route>



                    {/* 404 */}
                    <Route
                      path="*"
                      element={
                        <ProtectedRoute>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: "60vh",
                              flexDirection: "column",
                            }}
                          >
                            <h1>404 - Página no encontrada</h1>
                            <p>La página que buscas no existe.</p>
                          </div>
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                </Routes>
              </Suspense>
            </CourseStructureProvider>
          </FuncionProvider>
        </OutageProvider>
      </AuthProvider>
    </Router>
  )
}

export default App
